'use client';

import { Datepicker, TextField, Heading, Button, ButtonGroup, TextPassage, Drawer } from '@petsmart-ui/sparky';
import moment, { type Moment } from 'moment';
import Cookies from 'js-cookie';
import invariant from 'tiny-invariant';
import { type ChangeEvent, useState } from 'react';
import { dateToAz, validateTime } from './utils';
import { config } from './config';
import './PreviewDrawer.scss';

interface SitePreviewDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    previewDate?: string;
}
export function PreviewDrawer({ previewDate, isOpen, onClose }: SitePreviewDrawerProps) {
    const [date, setDate] = useState(() => {
        if (previewDate) {
            return moment(dateToAz(previewDate));
        }
        return null;
    });

    const [time, setTime] = useState(date?.format('HH:mm'));

    const onDateChange = (newDate: Moment | null) => {
        setDate(newDate);

        if (!time) {
            setTime('00:00');
        }
    };

    const onTimeChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        if (validateTime(target.value)) {
            setTime(target.value);
        }
    };

    const onReset = () => {
        setDate(null);
        setTime(undefined);
        Cookies.remove('preview-date');
        window.location.reload();
        onClose();
    };

    const onPreview = () => {
        invariant(date && time, 'date && time must be a defined');
        const [hours, minutes] = time.split(':');
        date.set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10) });
        Cookies.set('preview-date', date.format('YYYY-MM-DDTHH:mm:ss-07:00'));
        window.location.reload();
        onClose();
    };

    return (
        <Drawer isActive={isOpen} onClose={onClose}>
            <Drawer.Header>
                <Heading tagName="h5">Site Preview</Heading>
            </Drawer.Header>
            <Drawer.Body>
                <TextPassage>Date / Time</TextPassage>

                <div className="site-preview-pickers">
                    <Datepicker
                        date={date}
                        label="Date"
                        placeholder=""
                        numberOfMonths={1}
                        onDateChange={onDateChange}
                        isOutsideRange={() => false}
                        withFullScreenPortal={false}
                    />

                    <TextField
                        label="Time"
                        mask={config}
                        defaultValue={time ?? date?.format('HH:mm')}
                        onInput={onTimeChange}
                    />
                </div>
                <TextPassage className="site-preview-description">Time in: Arizona/Phoenix</TextPassage>
            </Drawer.Body>
            <Drawer.Footer>
                <ButtonGroup>
                    <Button text="Reset" onClick={onReset} fullWidth />
                    <Button text="Preview" variant="primary" onClick={onPreview} disabled={!date} fullWidth />
                </ButtonGroup>
            </Drawer.Footer>
        </Drawer>
    );
}
